import { IndividualBaseViewModel } from 'src/app/common/models/IndividualViewModel';
import { Component, OnInit, Input, EventEmitter, Output, SimpleChanges } from '@angular/core';
import { StringLengthLimiter } from '../../../common/helpers/string-length-limiter';
import { RootMemberResponse } from 'src/app/common/models/RootMember';
import { GuidGenerator } from 'src/app/common/helpers/guid-generator';
import { LoadingService } from 'src/app/services/UI/loading.service';
import { MultimediaTypes, EditorMode, ReferenceType, TreeLevel } from 'src/app/common/enums/enums';
import { CustomError } from 'src/app/common/helpers/custom-error';
import { StringLimiter } from 'src/app/common/helpers/string-limiter';
import { TranslateHandler } from 'src/app/common/helpers/translate-handler';
import { DialogService } from 'src/app/services/UI/dialog.service';
import { IndividualApiService } from 'src/app/services/API/individual-api.service';
import { Multimedia } from 'src/app/common/models/multimedia/multimedia';
import { NotifierEvents } from '../../../common/enums/enums';
import { NotifierV2Service } from 'src/app/services/notifier-v2.service';
type Phase = 'idle' | 'highlight' | 'travelToRoot' | 'release';
@Component({
    selector: 'g[app-mainlabels]',
    templateUrl: './mainlabels.component.html',
    styleUrls: ['./mainlabels.component.scss'],
    standalone: false
})
export class MainlabelsComponent implements OnInit {

  @Input() label: any;
  @Input() i: number;
  @Input() expandColor: any;
  @Input() rootMember: any;
  @Input() editedLabelImage: any;
  @Output() childFunction = new EventEmitter();
  @Output() openInputFormBoxWithData = new EventEmitter();
  @Output() refreshParent = new EventEmitter();
  selectedMember: any;
  @Input() selectedNodeId: any;
  @Input() allRelationsLoaded = false;  
  @Output() changeRootPerson = new EventEmitter<any>();
  multimediaSubscription: any;
  timeTag: string = "";
  splitDisplayName: any;

  @Input() isZoomedNode: boolean = false;
  @Input() animationPhase: Phase = 'idle';
  @Input() finishedRootNodeId: number | null = null;
  
  constructor(
    public stringLimit: StringLengthLimiter,
    private loadingService: LoadingService,
    public stringLimiter: StringLimiter,
    private translateHandler: TranslateHandler,
    private dialogService: DialogService,
    private individualApiService: IndividualApiService,
    private notifierService: NotifierV2Service) {
  }

  shouldHideLeaves(label: any): boolean {
    const focusId = this.selectedNodeId;
    if (focusId == null) return false;

    // Hide leaves for the whole "group" 
    const labelGroup = Math.floor(Number(label.displayIndex));
    const focusGroup = Math.floor(Number(focusId));

    return labelGroup === focusGroup;
  }

  breakText = function (text: String, line: number) {
    return this.stringLimit.breakText(text, line, this.limitLength);
  }

  ngOnInit(): void {
    this.setStringLimit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.editedLabelImage != null) {
      this.timeTag = "&timeTag=" + new Date().getTime(); // used to avoid caching
      if (this.label.profileImageId == this.editedLabelImage.id) {
        this.label.profileImage = this.editedLabelImage.mediaUrl;
      }
    }
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName)) {
        let self = this;// Self reference required 

        switch (propName) {
          case 'label': {
            if (this.label) {
              self.label = this.label;
              this.label.profileImage = this.label.profileImage ? this.label.profileImage + "&timeTag=" + new Date().getTime() : "";
            }
          }
        }
      }
    }
  }

  addImage(label: IndividualBaseViewModel) {
    this.dialogService.setHalf().open("Multimedia", {
      // Set id to 0 if StandAlone
      id: label.profileImageId,
      reference: {
        id: label.id, // Individual Id
        type: ReferenceType.Individual // Individual type
      },
      title: this.translateHandler.translate('lbl_link_media', [this.translateHandler.translate("lbl_pictures").toLowerCase(),
      this.label.givenName]) ?? "",
      mediaType: [MultimediaTypes.PHOTO],
      viewMode: 1, // Gallery or editor view
      editMediaId: 0, // MediaId which supposes to be edited
      isStandAlone: false
    }
    )
      .subscribe(response => {
        if (response.data) {
          let mediaBasic: Multimedia = null;

          response.data.deleteMedias.forEach(element => {
            this.label.profileImage = "";
            this.label.profileImageId = "";
            // Next image as primary one     
            mediaBasic = response.data.nextImage;
          });

          if (response.data.newMedia) {
            mediaBasic = new Multimedia();
            mediaBasic = response.data.newMedia;
          }

          if (mediaBasic != null) {
            let processId = GuidGenerator.generate();
            this.loadingService.show(processId);
            mediaBasic.isPrimary = true;
            mediaBasic.isNonProfile = false;
            //Update profile Image
            this.individualApiService.updateProFileImage(this.label.id, mediaBasic).subscribe((dataResponse: any) => {
              this.label.profileImage = mediaBasic.mediaUrl + "&timeTag=" + new Date().getTime();
              this.label.profileImageId = mediaBasic.id;
            }, (err) => {
              throw new CustomError("MainlabelComponent => addImage() : " + err, 911, false);
            }).add(() => {
              this.loadingService.hide(processId);
            });
          }

        }
      })
  }

  expandWheel(content) {
    this.childFunction.emit(true);
  }

  // A root-change (changeRootPerson) travel is under way and `label` is the node becoming the
  // new root. During 'highlight' the node hasn't started moving yet, so it stays at its current
  // size; from 'travelToRoot' onward we switch it to root sizing early (in step with the move
  // starting) rather than waiting for the async pedigree refresh that normally flips
  // displayIndex to 0 - that refresh lands well after the node has already glided into the root
  // position, so waiting for it produced a visible size jump right after arrival.
  private isRootTier(label: any): boolean {
    const becomingRoot = (this.animationPhase === 'travelToRoot' || this.animationPhase === 'release')
      && this.selectedNodeId != null
      && label.displayIndex === this.selectedNodeId;
    // The pedigree refetch that follows RootMemberChanged can resolve fast enough to null
    // selectedNodeId before the refreshed data has actually flipped this label's displayIndex
    // to 0 - a brief gap where becomingRoot goes false and displayIndex isn't 0 yet either,
    // reverting size for a frame or two before the data catches up and it snaps back. finishedRootNodeId
    // is set once (in changeRootPerson) at the same moment as becomingRoot's own trigger and stays
    // set across that whole gap (it's only cleared by the *next* changeRootPerson call), so it
    // bridges it without depending on selectedNodeId's timing.
    const settledRoot = this.finishedRootNodeId != null && label.displayIndex === this.finishedRootNodeId;
    return becomingRoot || settledRoot || label.displayIndex === 0;
  }

  // Root ~1.56x base, parents ~1.3x base, grandparents stay at base (1x)
  private photoScale(label: any): number {
    if (this.isRootTier(label)) return 1.56;
    if (label.displayIndex === 1 || label.displayIndex === 1.01) return 1.3;
    return 1;
  }

  photoScaleTransform(label: any): string {
    const scale = this.photoScale(label);
    return `translate(122.6,90.5) scale(${scale}) translate(-122.6,-90.5)`;
  }

  // Plus-toggle sits left of the photo circle; shift it out as the circle grows
  plusOffsetTransform(label: any): string {
    return this.photoScale(label) > 1 ? 'translate(-70,-9)' : 'translate(0,0)';
  }

  // Frame box left edge and name text shift right as the circle grows, so text clears the bigger photo
  frameLeftEdge(label: any): number {
    return this.isRootTier(label) ? 206 : (label.displayIndex === 1 || label.displayIndex === 1.01) ? 185.2 : 170.2;
  }

  // Right edge stays the same across tiers, preserving the existing vine/family-icon connector positions
  frameRightEdge(label: any): number {
    return 682.2;
  }

  nameTextTransform(label: any): string {
    const x = this.isRootTier(label) ? 266 : (label.displayIndex === 1 || label.displayIndex === 1.01) ? 245 : 230;
    return `translate(${x},105)`;
  }

  // Edit button hugs the right edge of the frame; shift it out as the circle grows
  editButtonTransform(label: any): string {
    const x = this.isRootTier(label) ? 646 : (label.displayIndex === 1 || label.displayIndex === 1.01) ? 641 : 626;
    return `translate(${x},75)`;
  }

  // Whole-node zoom level: root renders noticeably bigger (plus a compensating offset) than every
  // other tier. See isRootTier() for why this switches in step with travelToRoot rather than only
  // once displayIndex actually becomes 0.
  rootScaleTransform(label: any): string {
    return this.isRootTier(label) ? 'translate(-80, -10) scale(0.6)' : 'scale(0.47)';
  }

  editMember(label) {
    console.log("MemberEdit : MainLabels => EditMember(label) 1");
    let data = {
      id: label.id,
      editorMode: EditorMode.IndividualEditor,
      isUpdateHistory: true
    }
    let rootMember = this.selectedRootMember(label);
    this.notifierService.updateEvent(NotifierEvents.RootMemberChanged, rootMember);
    //JSON.stringify(data)
    this.notifierService.setCurrentEditor(`/editor/individualeditor`, data, null);
  }

  addParent(index, label) {
    this.openInputFormBoxWithData.emit({ index, label });
  }

  selectedRootMember(label): RootMemberResponse {
    var rootMember = new RootMemberResponse();
    rootMember.Id                 = label.id;
    rootMember.FirstName          = label.firstName;
    rootMember.LastName           = label.lastName;
    rootMember.DisplayName        = label.displayName;
    rootMember.Gender             = label.gender;
    rootMember.husbandDisplayName = label.husbandDisplayName;
    rootMember.wifeDisplayName    = label.wifeDisplayName;
    rootMember.defaultFamilyId    = label.defaultFamilyId

    return rootMember;
  }

  setStringLimit() {
    this.splitDisplayName = this.stringLimiter.splitString(this.label.displayName, this.textTreeLevel(this.label))
  }

  private textTreeLevel(label: any): TreeLevel {
    if (label.displayIndex === 0) return TreeLevel.RootMain;
    if (label.displayIndex === 1 || label.displayIndex === 1.01) return TreeLevel.ParentMain;
    return TreeLevel.Main;
  }

  updateFaultUrl() {
    this.label.profileImage = "";
  }

}
